/**
 * AKKREDITERING PÅ HUSETS ARRANGEMENT (sak 229.3, 0450) — huset og arrangøren fører den samme
 * lista, og bare huset krysser av for utlevert.
 *
 *   Huset:      `/v1/venue-nights/:id/accreditations[...]`, cellene `accreditation:*`.
 *   Arrangøren: `/v1/concerts/:concertId/night-accreditations[...]`, cellene `concert:read/update`.
 *
 * Arrangøren ser ikke husets kontaktpersoner eller hvem i huset som leverte ut: de to feltene er
 * alltid `null` i arrangørens svar.
 */
import { z } from 'zod';

const trimmed = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) => trimmed(max).nullish();

export const NIGHT_ACCREDITATION_REGISTERED_BY = ['venue', 'promoter'] as const;
export type NightAccreditationRegisteredBy = (typeof NIGHT_ACCREDITATION_REGISTERED_BY)[number];

export const MAX_NIGHT_ACCREDITATION_SEATS = 500;

export const nightAccreditationSchema = z
  .object({
    id: z.uuid(),
    venueNightId: z.uuid(),
    name: z.string(),
    company: z.string().nullable(),
    roleLabel: z.string().nullable(),
    /** Husets kontaktperson. Alltid null hos arrangøren. */
    contactPersonId: z.uuid().nullable(),
    contactPersonName: z.string().nullable(),
    accreditationTypeId: z.uuid().nullable(),
    accreditationTypeName: z.string().nullable(),
    seats: z.number().int(),
    handedOutCount: z.number().int(),
    handedOutAt: z.iso.datetime().nullable(),
    /** Hvem i huset som leverte ut. Alltid null hos arrangøren. */
    handedOutByName: z.string().nullable(),
    /** Hvilken side som førte raden, sett fra arrangementet. */
    registeredBy: z.enum(NIGHT_ACCREDITATION_REGISTERED_BY),
    registeredByOrgName: z.string(),
    registeredByPersonName: z.string().nullable(),
    /** Raden er ført av den som spør — arrangøren kan bare rette og fjerne sine egne. */
    mine: z.boolean(),
    note: z.string().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: 'NightAccreditation' });
export type NightAccreditation = z.infer<typeof nightAccreditationSchema>;

export const nightAccreditationTypeOptionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
});
export type NightAccreditationTypeOption = z.infer<typeof nightAccreditationTypeOptionSchema>;

/** Husets side: lista på arrangementet, typene som kan brukes, og «utlevert av totalt». */
export const nightAccreditationListSchema = z
  .object({
    items: z.array(nightAccreditationSchema),
    types: z.array(nightAccreditationTypeOptionSchema),
    seats: z.number().int(),
    handedOut: z.number().int(),
  })
  .meta({ id: 'NightAccreditationList' });
export type NightAccreditationList = z.infer<typeof nightAccreditationListSchema>;

/**
 * Arrangørens side. `venueNightId` er null til huset har bekreftet datoen — da finnes det ikke et
 * arrangement å akkreditere på ennå, og lista er tom.
 */
export const concertNightAccreditationsSchema = z
  .object({
    venueNightId: z.uuid().nullable(),
    houseName: z.string().nullable(),
    items: z.array(nightAccreditationSchema),
    types: z.array(nightAccreditationTypeOptionSchema),
    seats: z.number().int(),
    handedOut: z.number().int(),
  })
  .meta({ id: 'ConcertNightAccreditations' });
export type ConcertNightAccreditations = z.infer<typeof concertNightAccreditationsSchema>;

export const createNightAccreditationRequestSchema = z
  .object({
    name: trimmed(200).min(1),
    company: optionalText(200),
    roleLabel: optionalText(200),
    /** Bare huset. Arrangøren som sender en, får 400. */
    contactPersonId: z.uuid().nullish(),
    accreditationTypeId: z.uuid().nullish(),
    seats: z.number().int().min(1).max(MAX_NIGHT_ACCREDITATION_SEATS).optional(),
    note: optionalText(2000),
  })
  .meta({ id: 'CreateNightAccreditationRequest' });
export type CreateNightAccreditationRequest = z.infer<typeof createNightAccreditationRequestSchema>;

export const updateNightAccreditationRequestSchema = z
  .object({
    name: trimmed(200).min(1).optional(),
    company: optionalText(200),
    roleLabel: optionalText(200),
    contactPersonId: z.uuid().nullish(),
    accreditationTypeId: z.uuid().nullish(),
    seats: z.number().int().min(1).max(MAX_NIGHT_ACCREDITATION_SEATS).optional(),
    note: optionalText(2000),
  })
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field.' })
  .meta({ id: 'UpdateNightAccreditationRequest' });
export type UpdateNightAccreditationRequest = z.infer<typeof updateNightAccreditationRequestSchema>;

/**
 * «Utlevert»: antallet kort som er levert ut, ABSOLUTT og ikke pluss/minus (to i samme disk er det
 * vanlige, som i årgangens akkreditering). 0 tar alt tilbake.
 */
export const handOutNightAccreditationRequestSchema = z
  .object({
    handedOutCount: z.number().int().min(0).max(MAX_NIGHT_ACCREDITATION_SEATS),
  })
  .meta({ id: 'HandOutNightAccreditationRequest' });
export type HandOutNightAccreditationRequest = z.infer<
  typeof handOutNightAccreditationRequestSchema
>;
